import React from "react";
import DigitTicker from "../../components/DigitTicker";
import DecodeText from "../../components/DecodeText";
import FlashText from "../../components/FlashText";
import MatrixRain from "../../components/MatrixRain";
import type { CardProps, HeroProps, SectionHeaderProps } from "../types";
import "./matrix.css";

/**
 * MATRIX — the terminal.
 *
 * Everything is data and the interface is a console: monospace throughout,
 * prompt-prefixed headings with a blinking cursor, and cards rendered as
 * terminal output with status markers.
 */

/** Digits and katakana — the rain's own alphabet. */
const MX_GLYPHS = "01ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄ";

export const MatrixHero: React.FC<HeroProps> = ({ name, tagline }) => (
  <>
    <h1 className="hero-title mx-hero-title">
      <DigitTicker text={name.toUpperCase()} />
    </h1>
    <p className="hero-subtitle mx-hero-tagline">
      <span className="mx-prompt">$</span> {tagline}
    </p>
  </>
);

export const MatrixSectionHeader: React.FC<SectionHeaderProps> = ({
  title,
}) => (
  <h2 className="section-heading mx-section-heading">
    <span className="mx-prompt">&gt;</span>
    <span className="mx-section-label">
      <DecodeText text={title.toUpperCase()} once glyphs={MX_GLYPHS} />
    </span>
    <span className="mx-cursor" aria-hidden="true" />
  </h2>
);

export const MatrixCard: React.FC<CardProps> = ({ item }) => (
  <div className="item mx-card">
    <div className="mx-card-bar">
      <span className="mx-card-status">[OK]</span>
      {item.meta && (
        <span className="mx-card-meta">
          <DecodeText text={item.meta} once glyphs={MX_GLYPHS} />
        </span>
      )}
    </div>

    <h3 className="item-title mx-card-title">
      {item.href ? (
        <a
          className="item-title-link"
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <DecodeText text={item.title} once glyphs={MX_GLYPHS} />
        </a>
      ) : (
        <DecodeText text={item.title} once glyphs={MX_GLYPHS} />
      )}
    </h3>

    {item.subtitle && (
      <p className="mx-card-row">
        <span className="mx-card-key">role</span>
        <span className="mx-card-value">
          <DecodeText text={item.subtitle} once glyphs={MX_GLYPHS} />
        </span>
      </p>
    )}

    <p className="item-description mx-card-description">
      <FlashText text={item.description} />
    </p>

    {item.action && (
      <a
        href={item.action.href}
        target="_blank"
        rel="noopener noreferrer"
        className="item-link mx-card-action"
      >
        {item.action.label}
      </a>
    )}
  </div>
);

/** Phosphor stripes, scanlines and a CRT vignette — all pointer-transparent. */
export const MatrixChrome: React.FC = () => (
  <>
    {/* The vertical half of a CRT's structure. Scanlines alone give you
        horizontal banding, which reads as "old TV"; the aperture grille is
        what makes it read as a *monitor* you are sitting in front of. */}
    <div className="mx-grille" aria-hidden="true" />
    <div className="mx-scanlines" aria-hidden="true" />
    <div className="mx-vignette" aria-hidden="true" />
  </>
);

export const MatrixBackdrop = MatrixRain;
